// PRD 11 §2.2 #23 / §3.4 — 3-tier Area → Sub-area → Item cascade.
// Selecting Area resets Sub-area + Item; selecting Sub-area resets Item. The
// downstream selects are disabled until their parent is chosen.

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Select } from '../../../components/ui/Select';
import type { ManualCategoryTree } from '../types/counseling-manual.types';
import { getAreas, getItems, getSubAreas } from '../utils/category-tree';

export type CascadeField = 'area' | 'subArea' | 'item';

interface CascadingCategorySelectProps {
  tree: ManualCategoryTree;
  area: string;
  subArea: string;
  item: string;
  onChange: (next: { area: string; subArea: string; item: string }) => void;
  disabled?: boolean;
  // Blur-time validation hook ([공통] 00_입력검증표준 §1.2) — fires with the
  // field the user just left.
  onFieldBlur?: (field: CascadeField) => void;
}

export function CascadingCategorySelect({
  tree,
  area,
  subArea,
  item,
  onChange,
  disabled,
  onFieldBlur,
}: CascadingCategorySelectProps) {
  const { t } = useTranslation('counseling-manual');

  const areas = useMemo(() => getAreas(tree), [tree]);
  const subAreas = useMemo(() => getSubAreas(tree, area), [tree, area]);
  const items = useMemo(() => getItems(tree, area, subArea), [tree, area, subArea]);

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 8 }}>
      <Select
        options={areas}
        value={area}
        onChange={(value) => onChange({ area: value, subArea: '', item: '' })}
        onBlur={() => onFieldBlur?.('area')}
        disabled={disabled}
        placeholder={t('form.fields.area.placeholder')}
        ariaLabel={t('form.fields.area.label')}
      />

      <Select
        options={subAreas}
        value={subArea}
        onChange={(value) => onChange({ area, subArea: value, item: '' })}
        onBlur={() => onFieldBlur?.('subArea')}
        disabled={disabled || !area}
        placeholder={t('form.fields.subArea.placeholder')}
        ariaLabel={t('form.fields.subArea.label')}
      />

      <Select
        options={items}
        value={item}
        onChange={(value) => onChange({ area, subArea, item: value })}
        onBlur={() => onFieldBlur?.('item')}
        disabled={disabled || !subArea}
        placeholder={t('form.fields.item.placeholder')}
        ariaLabel={t('form.fields.item.label')}
      />
    </div>
  );
}
